import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import { CloseButton } from '../CloseButton';
import { useDevice } from '../Device';
import { Divider } from '../Divider';
import { getMobileTheme, ScopedThemeProvider, useTheme } from '../Theme';
import { Typography } from '../Typography';
import styles from './FullPageModal.module.scss';
const HeaderStartContext = /*#__PURE__*/ React.createContext(null);
const HeaderEndContext = /*#__PURE__*/ React.createContext(null);
export function FullPageModal(props) {
    const { children, className, contentClassName, headerClassName, contentSkin = 'default', titleVariant = 'h5', testId, onClose, scrollContainerRef } = props, rest = _object_without_properties(props, [
        "children",
        "className",
        "contentClassName",
        "headerClassName",
        "contentSkin",
        "titleVariant",
        "testId",
        "onClose",
        "scrollContainerRef"
    ]);
    const { isMobileDevice } = useDevice();
    const parentTheme = useTheme();
    const [headerStartEl, setHeaderStartEl] = useState(null);
    const [headerEndEl, setHeaderEndEl] = useState(null);
    const closeBtn = /*#__PURE__*/ _jsx(CloseButton, {
        // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: FullPageModal owns the inherent fixed-semantic "close" action; consumers cannot rename it (rule 6a), so the name is hardcoded by design, not a missing pass-through.
        bi: {
            name: 'close'
        },
        skin: "transparent",
        testId: testId && testId + '-close-btn',
        onClick: onClose
    });
    const wrapWithProviders = (children)=>/*#__PURE__*/ _jsx(HeaderStartContext.Provider, {
            value: headerStartEl,
            children: /*#__PURE__*/ _jsx(HeaderEndContext.Provider, {
                value: headerEndEl,
                children: children
            })
        });
    const wrapWithMobileTheme = (children)=>isMobileDevice ? /*#__PURE__*/ _jsx(ScopedThemeProvider, {
            theme: _object_spread({}, parentTheme, getMobileTheme()),
            testId: testId && "".concat(testId, "-content-theme"),
            children: children
        }) : children;
    return /*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({}, rest), {
        className: cn(styles.root, className, {
            [styles.mobile]: isMobileDevice
        }),
        "data-testid": testId,
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: cn(styles.header, headerClassName),
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: styles.flexContainer,
                        children: [
                            closeBtn,
                            /*#__PURE__*/ _jsx(Divider, {
                                orientation: "vertical",
                                margin: 8,
                                size: 32
                            }),
                            /*#__PURE__*/ _jsx(Typography, {
                                variant: titleVariant,
                                weight: "normal",
                                noWrap: isMobileDevice,
                                className: cn(styles.flexContainer, styles.headerTitle, {
                                    [styles.headerTitleMobile]: isMobileDevice,
                                    [styles.headerTitleMobileH6]: isMobileDevice && titleVariant === 'h6'
                                }),
                                ref: setHeaderStartEl,
                                testId: testId && testId + '-header-start'
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.flexContainer, styles.headerEnd),
                        children: /*#__PURE__*/ _jsx("div", {
                            ref: setHeaderEndEl,
                            className: styles.flexContainer,
                            "data-testid": testId && "".concat(testId, "-header-end")
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("div", {
                className: cn(styles.content, styles["".concat(contentSkin, "Skin")], contentClassName),
                ref: scrollContainerRef,
                "data-testid": testId && "".concat(testId, "-content"),
                children: wrapWithMobileTheme(wrapWithProviders(children))
            })
        ]
    }));
}
const useSlot = (context, children)=>{
    const element = useContext(context);
    return element ? /*#__PURE__*/ createPortal(children, element) : null;
};
export const FullPageModalHeaderStart = (p)=>useSlot(HeaderStartContext, p.children);
export const FullPageModalHeaderEnd = (p)=>useSlot(HeaderEndContext, p.children);
export function FullPageModalMainActions(props) {
    return useSlot(HeaderEndContext, /*#__PURE__*/ _jsx("div", {
        className: styles.flexContainer,
        children: props.children
    }));
}
FullPageModal.HeaderStart = FullPageModalHeaderStart;
FullPageModal.HeaderEnd = FullPageModalHeaderEnd;
FullPageModal.MainActions = FullPageModalMainActions;
